import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Plus, Search } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { ApiError } from "@hostdock/sdk";
import type { CreatedWithPassword, CustomerRecord } from "@hostdock/types";
import {
  Button, DataTable, Dialog, EmptyState, Field, FormError, formatDate, Input, OneTimeSecret, PageHeader, Pagination, Panel, Select, StatusBadge,
} from "@hostdock/ui";
import { api } from "../api";
import { RequirePermission } from "../lib/guard";
import { useListParams } from "../lib/list-params";
import { useSession } from "../session";

const PAGE_SIZE = 25;

export function CustomersPage() {
  return (
    <RequirePermission anyOf={["CUSTOMER_VIEW"]}>
      <CustomersList />
    </RequirePermission>
  );
}

function CustomersList() {
  const { can } = useSession();
  const navigate = useNavigate();
  const { values, page, set } = useListParams(["search", "status"] as const);
  const [search, setSearch] = useState(values.search);
  const [creating, setCreating] = useState(false);
  const q = useQuery({
    queryKey: ["customers", values, page],
    queryFn: () => api.customers.list({ ...values, page, pageSize: PAGE_SIZE }),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <PageHeader
        title="Customers"
        description="Hosting accounts and their owners."
        actions={can("CUSTOMER_MANAGE") && <Button variant="primary" icon={<Plus size={15} />} onClick={() => setCreating(true)}>New customer</Button>}
      />
      <Panel flush>
        <div className="flex flex-wrap items-center gap-2 border-b border-line p-3">
          <form
            className="relative min-w-52 flex-1"
            onSubmit={(e) => {
              e.preventDefault();
              set({ search: search.trim() });
            }}
          >
            <Search size={14} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-subtle" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, company or email" className="pl-8" aria-label="Search customers" />
          </form>
          <Select value={values.status} onChange={(e) => set({ status: e.target.value })} className="w-40" aria-label="Filter by status">
            <option value="">All statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="SUSPENDED">Suspended</option>
            <option value="CLOSED">Closed</option>
          </Select>
        </div>
        <DataTable
          rows={q.data?.items}
          loading={q.isPending}
          error={q.error}
          onRetry={() => void q.refetch()}
          rowKey={(c) => c.id}
          onRowClick={(c) => navigate(`/customers/${c.id}`)}
          empty={
            values.search || values.status ? (
              <EmptyState icon={Search} title="No matching customers" description="Try a different search or filter." />
            ) : (
              <EmptyState icon={Building2} title="No customers yet" description="Customers appear here when you create them or when a billing integration provisions an account." />
            )
          }
          columns={[
            {
              key: "name",
              header: "Customer",
              cell: (c) => (
                <div className="min-w-0">
                  <p className="truncate font-medium">{c.displayName}</p>
                  {c.companyName && <p className="truncate text-[12px] text-muted">{c.companyName}</p>}
                </div>
              ),
            },
            { key: "owner", header: "Owner", cell: (c) => <span className="text-muted">{c.owner.email}</span>, hideOnMobile: true },
            { key: "status", header: "Status", cell: (c) => <StatusBadge status={c.status} /> },
            { key: "mfa", header: "2FA", cell: (c) => <span className={c.owner.mfaEnabled ? "text-success" : "text-subtle"}>{c.owner.mfaEnabled ? "On" : "Off"}</span>, hideOnMobile: true },
            { key: "created", header: "Created", cell: (c) => <span className="text-muted">{formatDate(c.createdAt)}</span>, hideOnMobile: true },
          ]}
        />
        {q.data && <Pagination page={page} pageSize={PAGE_SIZE} total={q.data.total} onPage={(p) => set({ page: String(p) })} />}
      </Panel>
      <CreateCustomerDialog open={creating} onOpenChange={setCreating} />
    </>
  );
}

function CreateCustomerDialog({ open, onOpenChange }: { open: boolean; onOpenChange(o: boolean): void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ displayName: "", companyName: "", name: "", email: "", phone: "" });
  const [created, setCreated] = useState<CreatedWithPassword<CustomerRecord> | null>(null);
  const create = useMutation({ meta: { inlineError: true },
    mutationFn: () =>
      api.customers.create({
        displayName: form.displayName,
        name: form.name,
        email: form.email,
        companyName: form.companyName || null,
        phone: form.phone || null,
      }),
    onSuccess: (res) => {
      setCreated(res);
      void qc.invalidateQueries({ queryKey: ["customers"] });
      void qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
  const close = (o: boolean) => {
    if (!o) {
      setForm({ displayName: "", companyName: "", name: "", email: "", phone: "" });
      setCreated(null);
      create.reset();
    }
    onOpenChange(o);
  };
  const fe = create.error instanceof ApiError ? create.error.fieldErrors : {};
  const upd = (k: keyof typeof form) => (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={close}
      title={created ? "Customer created" : "New customer"}
      description={created ? `${created.record.displayName} can now sign in to the HostDock Panel.` : "Creates the account and its owner login."}
      footer={created ? <Button variant="primary" onClick={() => close(false)}>Done</Button> : undefined}
    >
      {created ? (
        <OneTimeSecret label={`Temporary password for ${created.record.owner.email}`} value={created.temporaryPassword} note="The customer must change it at first sign-in. Copy it now and share it through a secure channel." />
      ) : (
        <form id="create-customer" onSubmit={submit} className="flex flex-col gap-4" noValidate>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Account name" error={fe.displayName}>{(p) => <Input {...p} value={form.displayName} onChange={upd("displayName")} placeholder="Acme Studio" autoFocus />}</Field>
            <Field label="Company (optional)" error={fe.companyName}>{(p) => <Input {...p} value={form.companyName} onChange={upd("companyName")} />}</Field>
            <Field label="Owner full name" error={fe.name}>{(p) => <Input {...p} value={form.name} onChange={upd("name")} autoComplete="off" />}</Field>
            <Field label="Owner email" error={fe.email}>{(p) => <Input {...p} type="email" value={form.email} onChange={upd("email")} autoComplete="off" />}</Field>
            <Field label="Phone (optional)" error={fe.phone}>{(p) => <Input {...p} value={form.phone} onChange={upd("phone")} />}</Field>
          </div>
          {create.error && !Object.keys(fe).length ? <FormError error={create.error} /> : null}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => close(false)}>Cancel</Button>
            <Button type="submit" variant="primary" loading={create.isPending} disabled={!form.displayName || !form.name || !form.email}>
              Create customer
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
